/**
 * SC-730 — couleur d'une catégorie de la carte.
 *
 * La couleur vient d'abord de l'admin (`Category.background_color`, déjà
 * éditable dans Catalogue → fiche catégorie et déjà synchronisée vers la
 * caisse). C'est ce qui permet à un restaurant de donner LA MÊME couleur à
 * toutes les catégories d'une même famille — entrées froides + entrées
 * chaudes = un seul vert — qui est le repère que cherche le serveur.
 *
 * Sans couleur choisie on retombe sur une palette fixe indexée par un hash de
 * l'uuid : stable dans le temps (ajouter une catégorie ne redistribue pas les
 * couleurs des autres, la mémoire visuelle du serveur tient) et jamais grise,
 * donc le repérage marche déjà sur un catalogue que personne n'a configuré.
 */

/** Teintes moyennes, volontairement espacées en teinte ET en luminance, pour
 *  rester distinguables côte à côte sur un écran de téléphone en salle. */
export const FALLBACK_CATEGORY_COLORS = [
  '#E4572E',
  '#F4A259',
  '#EBC334',
  '#7FB069',
  '#2A9D8F',
  '#4C8FBD',
  '#6C5CA8',
  '#C56AA8',
  '#A0522D',
  '#5C8001',
] as const;

const HEX6 = /^#[0-9a-fA-F]{6}$/;
const HEX3 = /^#[0-9a-fA-F]{3}$/;

/** `#abc` → `#aabbcc`; laisse passer `#aabbcc`; rejette le reste. */
function normaliseHex(value: string | undefined): string | null {
  const hex = (value ?? '').trim();
  if (HEX6.test(hex)) return hex;
  if (HEX3.test(hex)) return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  return null;
}

/** djb2-ish : déterministe, sans dépendance, suffisant pour répartir des uuid. */
function hashToIndex(id: string, buckets: number): number {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) {
    h = (h * 31 + id.charCodeAt(i)) >>> 0;
  }
  return h % buckets;
}

const TEXT_DARK = '#1D1E1E';
const TEXT_LIGHT = '#FFFFFF';

function channel(value: number): number {
  const s = value / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function luminance(hex: string): number {
  const h = hex.replace('#', '');
  return (
    0.2126 * channel(parseInt(h.slice(0, 2), 16)) +
    0.7152 * channel(parseInt(h.slice(2, 4), 16)) +
    0.0722 * channel(parseInt(h.slice(4, 6), 16))
  );
}

function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Texte lisible sur une couleur de categorie.
 *
 * On compare les DEUX candidats au lieu de basculer sur un seuil de luminance
 * fixe : sur les teintes moyennes (rouge brique, teal, bleu) un seuil se trompe
 * de cote et tombe a ~3,3:1, sous le 4,5:1 demande pour du petit texte. Mesure
 * du 2026-08-24 sur la palette de repli : 4 entrees sur 10 etaient concernees,
 * plancher remonte a 4,54:1 en prenant le meilleur des deux.
 *
 * Une couleur choisie en admin pile au milieu de la plage peut rester sous
 * 4,5:1 des deux cotes — c'est alors le choix du restaurateur, on lui rend le
 * moins mauvais des deux.
 */
export function onCategoryColor(hex: string): string {
  const h = normaliseHex(hex);
  if (!h) return TEXT_LIGHT;
  return contrast(h, TEXT_DARK) >= contrast(h, TEXT_LIGHT) ? TEXT_DARK : TEXT_LIGHT;
}

export function categoryColor(category: { id: string; color?: string }): string {
  return (
    normaliseHex(category.color) ??
    FALLBACK_CATEGORY_COLORS[hashToIndex(category.id, FALLBACK_CATEGORY_COLORS.length)]
  );
}
